import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError, api } from '../api/client.js'
import type { ObjectDetail, ObjectKind, Plan, Template } from '../api/types.js'
import { KIND_LABEL, KIND_PLURAL, ObjectPicker, childKinds } from '../components/objects.js'
import { AssigneePicker } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { Field, Modal, Notice } from '../components/ui.js'
import { formatDate, todayIso } from '../lib/format.js'
import { expandRecurrence } from '@hauswerk/shared'
import { RecurrenceEditor, SeasonEditor, type Recurrence } from './RecurrenceEditor.js'

export interface PlanDraft {
  /** Set when an existing plan is edited. */
  id?: string
  objectId: string
  spreadKind: ObjectKind | ''
  templateId: string
  assigneeId: string
  assigneeTeamId: string
  recurrence: Recurrence
  startDate: string
  endDate: string
  seasonStart: string | null
  seasonEnd: string | null
  dueTime: string
}

export function draftFromPlan(plan: Plan): PlanDraft {
  return {
    id: plan.id,
    objectId: plan.objectId,
    spreadKind: plan.spreadKind ?? '',
    templateId: plan.templateId,
    assigneeId: plan.assigneeId ?? '',
    assigneeTeamId: plan.assigneeTeamId ?? '',
    recurrence: plan.recurrence,
    startDate: plan.startDate,
    endDate: plan.endDate ?? '',
    seasonStart: plan.seasonStart,
    seasonEnd: plan.seasonEnd,
    dueTime: plan.dueTime ?? '',
  }
}

export function emptyPlanDraft(objectId = ''): PlanDraft {
  return {
    objectId,
    spreadKind: '',
    templateId: '',
    assigneeId: '',
    assigneeTeamId: '',
    recurrence: { type: 'weekly', weekdays: [2] },
    startDate: todayIso(),
    endDate: '',
    seasonStart: null,
    seasonEnd: null,
    dueTime: '',
  }
}

/** The plural for "jede Einheit darunter" style labels. */
function eachLabel(kind: ObjectKind): string {
  return { customer: 'jeder Kunde', site: 'jede Liegenschaft', building: 'jedes Gebäude', unit: 'jede Einheit', component: 'jedes Bauteil' }[kind]
}

/**
 * One form for new and existing plans, opened from the plan list, from an
 * object in the explorer, or from a task. "Gilt für" decides whether the plan
 * produces one task on the chosen object or one per object of a level below
 * it; the counts next to each option come from the tree as it stands today.
 */
export function PlanFormModal({
  draft,
  onClose,
  onSaved,
}: {
  draft: PlanDraft | null
  onClose: () => void
  onSaved: () => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [form, setForm] = useState<PlanDraft | null>(draft)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setForm(draft)
    setError(null)
  }, [draft])

  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/templates') })
  const anchor = useQuery({
    queryKey: ['object', form?.objectId],
    queryFn: () => api<ObjectDetail>(`/api/objects/${form!.objectId}`),
    enabled: Boolean(form?.objectId),
  })

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['plans'] })
    await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
    await queryClient.invalidateQueries({ queryKey: ['object'] })
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<{ generated: number }>('/api/plans', { method: 'POST', body }),
    onSuccess: async (result) => {
      toast(`Plan angelegt. ${result.generated} Aufgaben wurden für die kommenden Wochen erzeugt.`)
      await invalidate()
      onSaved()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api<{ removedFutureTasks: number; keptAdjustedTasks: number }>(`/api/plans/${id}`, { method: 'PATCH', body }),
    onSuccess: async (result) => {
      toast(
        (result.removedFutureTasks > 0
          ? `Plan geändert. ${result.removedFutureTasks} künftige offene Aufgaben wurden nach der neuen Regel neu erzeugt.`
          : 'Plan geändert.') + keptNote(result.keptAdjustedTasks),
      )
      await invalidate()
      onSaved()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!form) return
    if (!form.objectId) {
      setError('Bitte ein Objekt wählen.')
      return
    }
    if (anchor.data?.kind === 'customer' && !form.spreadKind) {
      setError('Am Kunden braucht der Plan eine Ebene darunter, für die er gilt.')
      return
    }
    const body = {
      objectId: form.objectId,
      spreadKind: form.spreadKind || null,
      templateId: form.templateId,
      assigneeId: form.assigneeTeamId ? null : form.assigneeId || null,
      assigneeTeamId: form.assigneeTeamId || null,
      recurrence: form.recurrence,
      startDate: form.startDate,
      endDate: form.endDate || null,
      seasonStart: form.seasonStart,
      seasonEnd: form.seasonEnd,
      dueTime: form.dueTime || null,
      active: true,
    }
    if (form.id) update.mutate({ id: form.id, body })
    else create.mutate(body)
  }

  const anchorKind = anchor.data?.kind ?? null
  const below = anchorKind ? childKinds(anchorKind) : []
  const counts = anchor.data?.kindCounts ?? {}
  const targetCount = form?.spreadKind ? (counts[form.spreadKind] ?? 0) : 1

  return (
    <Modal
      open={draft !== null}
      title={draft?.id ? 'Aufgabenplan bearbeiten' : 'Neuer Aufgabenplan'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="plan-form" className="btn" disabled={create.isPending || update.isPending}>
            {draft?.id ? 'Speichern' : 'Plan anlegen'}
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      {form && (
        <form id="plan-form" onSubmit={submit}>
          {form.id && (
            <Notice kind="info">
              Änderungen gelten ab heute: künftige, noch offene Aufgaben werden nach der neuen Regel neu erzeugt. Erledigtes und
              Vergangenes bleibt, wie es war.
            </Notice>
          )}
          <Field label="Leistung">
            <select required value={form.templateId} onChange={(e) => setForm({ ...form, templateId: e.target.value })}>
              <option value="">Bitte wählen …</option>
              {templates.data?.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Wo?" hint="Kunde, Liegenschaft, Gebäude oder Einheit.">
            <ObjectPicker value={form.objectId} onChange={(id) => setForm({ ...form, objectId: id, spreadKind: '' })} taskable={false} />
          </Field>

          {anchorKind && (
            <Field
              label="Gilt für"
              hint={
                form.spreadKind
                  ? `Erzeugt je Termin ${targetCount} Aufgaben, eine pro ${KIND_LABEL[form.spreadKind]}. Objekte, die später dazukommen, werden automatisch aufgenommen.`
                  : anchorKind === 'customer'
                    ? 'Ein Kunde trägt selbst keine Aufgaben. Bitte eine Ebene darunter wählen.'
                    : 'Eine Aufgabe je Termin auf genau diesem Objekt.'
              }
            >
              <select value={form.spreadKind} onChange={(e) => setForm({ ...form, spreadKind: e.target.value as ObjectKind | '' })}>
                {anchorKind !== 'customer' && <option value="">Dieses Objekt ({anchor.data?.name})</option>}
                {below.map((kind) => (
                  <option key={kind} value={kind} disabled={(counts[kind] ?? 0) === 0}>
                    {eachLabel(kind)} darunter ({counts[kind] ?? 0} {KIND_PLURAL[kind]})
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field
            label="Zuständig"
            hint="Eine Person, oder eine Gruppe: dann sehen alle in der Gruppe die Aufgabe, wer zuerst abhakt, hat sie erledigt. Pro Einzelaufgabe änderbar."
          >
            <AssigneePicker
              value={{ assigneeId: form.assigneeId || null, assigneeTeamId: form.assigneeTeamId || null }}
              onChange={(v) => setForm({ ...form, assigneeId: v.assigneeId ?? '', assigneeTeamId: v.assigneeTeamId ?? '' })}
              emptyLabel="Noch nicht zugewiesen"
            />
          </Field>

          <hr style={{ border: 0, borderTop: '1px solid var(--line-soft)', margin: '18px 0' }} />

          <RecurrenceEditor value={form.recurrence} onChange={(recurrence) => setForm({ ...form, recurrence })} />

          <div className="row">
            <Field label="Gültig ab">
              <input type="date" required value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
            </Field>
            <Field label="Gültig bis" hint="Leer lassen für unbefristet.">
              <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
            </Field>
          </div>

          <Field label="Uhrzeit" hint="Nur ein Hinweis für den Mitarbeiter, keine harte Frist.">
            <input type="time" value={form.dueTime} onChange={(e) => setForm({ ...form, dueTime: e.target.value })} />
          </Field>

          <SeasonEditor start={form.seasonStart} end={form.seasonEnd} onChange={(seasonStart, seasonEnd) => setForm({ ...form, seasonStart, seasonEnd })} />
          <PlanPreview form={form} />
        </form>
      )}
    </Modal>
  )
}

/** "jede Einheit darunter (24)" for lists; empty for a plain plan. */
export function spreadLabel(plan: Pick<Plan, 'spreadKind' | 'targetCount'>): string | null {
  if (!plan.spreadKind) return null
  return `${eachLabel(plan.spreadKind)} darunter (${plan.targetCount})`
}

/**
 * Wann der Plan im kommenden Jahr Aufgaben erzeugt. Erzeugt werden sie nur
 * ein paar Wochen im Voraus; die Vorschau zeigt trotzdem das ganze Jahr.
 */
function PlanPreview({ form }: { form: PlanDraft }) {
  const today = todayIso()
  const from = form.startDate > today ? form.startDate : today
  const to = new Date(Date.parse(`${from}T12:00:00Z`) + 365 * 86_400_000).toISOString().slice(0, 10)
  let dates: string[] = []
  try {
    dates = expandRecurrence(
      { recurrence: form.recurrence as never, startDate: form.startDate, endDate: form.endDate || null, seasonStart: form.seasonStart, seasonEnd: form.seasonEnd },
      from,
      to,
    )
  } catch {
    return null
  }
  const months = new Map<string, number>()
  for (const d of dates) months.set(d.slice(0, 7), (months.get(d.slice(0, 7)) ?? 0) + 1)
  const monthName = (key: string) => new Intl.DateTimeFormat('de-DE', { month: 'short' }).format(new Date(`${key}-15T12:00:00Z`))
  return (
    <div className="plan-preview">
      <div className="strong small">Termine in den nächsten 12 Monaten: {dates.length}</div>
      {dates.length > 0 && (
        <>
          <div className="small muted">Nächste: {dates.slice(0, 6).map((d) => formatDate(d)).join(', ')}</div>
          <div className="plan-months">
            {[...months.entries()].map(([key, n]) => (
              <span key={key} className="plan-month" title={`${n} Termine`}>
                <span className="muted">{monthName(key)}</span> {n}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/** Tasks changed by hand (Vertretung, moved date) are not rebuilt by a plan change. */
export function keptNote(kept: number | undefined): string {
  if (!kept) return ''
  return kept === 1
    ? ' Eine von Hand geänderte Aufgabe bleibt, wie sie ist.'
    : ` ${kept} von Hand geänderte Aufgaben bleiben, wie sie sind.`
}
